import { useCallback, useEffect, useState } from 'react';

/**
 * Routing, in about forty lines.
 *
 * Maestro has one navigation shape — a project, then a screen, then sometimes
 * one thing on that screen — and the History API does all of it. A router
 * library would be more code than this and one more thing to keep up to date.
 *
 * A route is a path like `/p/SAMPLE/board` or `/takes/<id>`. The share page
 * `/share/<token>` is deliberately outside the app shell: rule 13 says the
 * outside page has no route into the rest of Maestro.
 */

export function useRoute(): { path: string; go: (to: string, replace?: boolean) => void } {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPop = (): void => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const go = useCallback((to: string, replace = false) => {
    if (to === window.location.pathname) return;
    window.history[replace ? 'replaceState' : 'pushState']({}, '', to);
    setPath(to);
    window.scrollTo(0, 0);
  }, []);

  return { path, go };
}

/** The parts of a path, with the empties dropped. */
export function segments(path: string): string[] {
  return path.split('/').filter((s) => s !== '');
}

/**
 * Which project a path is about, and which screen.
 *
 * Everything in Maestro is scoped by project (docs/06, decision 10), so the
 * project is in the path rather than in a global somewhere — a link somebody
 * pastes into a message has to open the same thing for whoever clicks it.
 */
export interface Where {
  projectId: string | null;
  screen: string;
  /** The one thing being looked at, when the screen is about one thing. */
  id: string | null;
}

export function readPath(path: string): Where {
  const parts = segments(path);
  if (parts[0] === 'p' && parts[1]) {
    return { projectId: parts[1], screen: parts[2] ?? 'board', id: parts[3] ?? null };
  }
  if (parts[0] === 'takes' && parts[1]) {
    return { projectId: null, screen: 'take', id: parts[1] };
  }
  if (parts[0] === 'share' && parts[1]) {
    return { projectId: null, screen: 'share', id: parts[1] };
  }
  return { projectId: null, screen: parts[0] ?? 'projects', id: parts[1] ?? null };
}

export function pathFor(projectId: string, screen: string, id?: string): string {
  return `/p/${projectId}/${screen}${id ? `/${id}` : ''}`;
}
